import { Check, X } from '@phosphor-icons/react'
import { useEffect, useId, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { LoadingState } from '@/components/loading-state'
import { ApiError } from '@/lib/api/errors'
import { friendlyErrorKey } from '@/lib/api/friendly-errors'
import { usePersonNameFormatter } from '@/lib/users/use-person-name'
import { kuniriIconWeight } from '@/lib/icons'

import { activityDetailApi, type ActivityDetail, type JoinRequest } from '../api'
import { canEditActivity } from '../access'
import { ConfirmDialog } from './confirm-dialog'
import type { ActivityNotice } from './feedback-notice'
import { UserAvatar } from './user-avatar'

interface JoinRequestsPanelProps {
  activity: ActivityDetail
  onChanged: () => void
  onNotice: (notice: ActivityNotice) => void
}

type Decision = { action: 'accept' | 'reject'; request: JoinRequest }

/**
 * Creator-only pending join requests (API_CONTRACT §6.12). The list is only
 * requested before the start; accept/reject always require a confirmation and
 * refresh both the list and the activity detail afterwards.
 */
export function JoinRequestsPanel({
  activity,
  onChanged,
  onNotice,
}: JoinRequestsPanelProps) {
  const { t } = useTranslation()
  const formatPerson = usePersonNameFormatter()
  const headingId = useId()
  const reasonId = useId()
  const enabled = canEditActivity(activity)
  const [status, setStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>(
    'idle',
  )
  const [items, setItems] = useState<JoinRequest[]>([])
  const [reloadKey, setReloadKey] = useState(0)
  const [decision, setDecision] = useState<Decision | null>(null)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!enabled) return
    const controller = new AbortController()

    void activityDetailApi
      .listJoinRequests(activity.id, { signal: controller.signal })
      .then((response) => {
        if (controller.signal.aborted) return
        setItems(response.items)
        setStatus('ready')
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return
        if (error instanceof ApiError && error.code === 'ABORTED') return
        setStatus('error')
      })

    return () => controller.abort()
  }, [activity.id, enabled, reloadKey])

  function openDecision(action: Decision['action'], request: JoinRequest) {
    setError(null)
    setReason('')
    setDecision({ action, request })
  }

  async function submitDecision() {
    if (!decision || busy) return
    setBusy(true)
    setError(null)
    try {
      if (decision.action === 'accept') {
        await activityDetailApi.acceptJoinRequest(activity.id, decision.request.id)
        onNotice({ tone: 'success', key: 'activityDetail.requests.acceptSuccess' })
      } else {
        await activityDetailApi.rejectJoinRequest(
          activity.id,
          decision.request.id,
          reason,
        )
        onNotice({ tone: 'success', key: 'activityDetail.requests.rejectSuccess' })
      }
      setDecision(null)
      setReason('')
      onChanged()
      setStatus('loading')
      setReloadKey((key) => key + 1)
    } catch (error) {
      setError(friendlyErrorKey(error))
    } finally {
      setBusy(false)
    }
  }

  if (!enabled) return null

  if (status === 'idle' || (status === 'loading' && items.length === 0)) {
    return (
      <section
        aria-label={t('activityDetail.requests.title')}
        className="space-y-3"
      >
        <LoadingState compact labelKey="activityDetail.requests.loading" />
      </section>
    )
  }

  if (status === 'error' && items.length === 0) {
    return (
      <section
        aria-label={t('activityDetail.requests.title')}
        className="space-y-3"
        role="alert"
      >
        <p className="text-sm text-muted">
          {t('activityDetail.requests.loadError')}
        </p>
        <button
          type="button"
          className="btn-ghost mt-1 min-h-11 px-2 text-sm"
          onClick={() => {
            setStatus('loading')
            setReloadKey((key) => key + 1)
          }}
        >
          {t('activityDetail.requests.retry')}
        </button>
      </section>
    )
  }

  if (items.length === 0) return null

  return (
    <section
      aria-labelledby={headingId}
      aria-busy={status === 'loading' || undefined}
      className="space-y-3"
    >
      <h2
        id={headingId}
        className="font-head text-[18px] font-extrabold text-deep"
      >
        {t('activityDetail.requests.title')}
      </h2>

      {status === 'loading' ? (
        <LoadingState compact labelKey="activityDetail.requests.loading" />
      ) : null}

      <ul className="mt-2 divide-y divide-line">
        {items.map((request) => (
          <li key={request.id}>
            <div className="flex items-center gap-3 py-3">
              <UserAvatar name={formatPerson(request)} src={null} />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold break-words text-deep">
                  {formatPerson(request)}
                </p>
                {request.city || request.bio ? (
                  <p className="mt-0.5 text-xs leading-relaxed break-words text-muted">
                    {[request.city, request.bio].filter(Boolean).join(' · ')}
                  </p>
                ) : null}
              </div>
              <div className="flex shrink-0 gap-1">
                <button
                  type="button"
                  className="btn-ghost size-11 justify-center rounded-full p-0 text-teal"
                  aria-label={t('activityDetail.requests.acceptFor', {
                    name: formatPerson(request),
                  })}
                  onClick={() => openDecision('accept', request)}
                >
                  <Check
                    size={20}
                    weight={kuniriIconWeight}
                    aria-hidden="true"
                  />
                </button>
                <button
                  type="button"
                  className="btn-ghost size-11 justify-center rounded-full p-0 text-danger hover:bg-[var(--danger-soft)]"
                  aria-label={t('activityDetail.requests.rejectFor', {
                    name: formatPerson(request),
                  })}
                  onClick={() => openDecision('reject', request)}
                >
                  <X size={20} weight={kuniriIconWeight} aria-hidden="true" />
                </button>
              </div>
            </div>
          </li>
        ))}
      </ul>

      <ConfirmDialog
        open={decision !== null}
        titleKey={
          decision?.action === 'reject'
            ? 'activityDetail.requests.rejectTitle'
            : 'activityDetail.requests.acceptTitle'
        }
        titleValues={{ name: formatPerson(decision?.request) }}
        bodyKey={
          decision?.action === 'reject'
            ? 'activityDetail.requests.rejectBody'
            : 'activityDetail.requests.acceptBody'
        }
        bodyValues={{ name: formatPerson(decision?.request) }}
        confirmKey={
          decision?.action === 'reject'
            ? 'activityDetail.requests.rejectConfirm'
            : 'activityDetail.requests.acceptConfirm'
        }
        cancelKey="activityDetail.requests.cancel"
        tone={decision?.action === 'reject' ? 'danger' : 'default'}
        busy={busy}
        onConfirm={() => void submitDecision()}
        onCancel={() => {
          if (busy) return
          setDecision(null)
          setReason('')
        }}
      >
        {decision?.action === 'reject' ? (
          <div>
            <label htmlFor={reasonId} className="field-label text-sm">
              {t('activityDetail.requests.rejectReason')}
            </label>
            <textarea
              id={reasonId}
              className="field-input min-h-20"
              maxLength={500}
              rows={2}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </div>
        ) : null}
        {error ? (
          <p role="alert" className="mt-3 text-sm font-semibold text-danger">
            {t(error)}
          </p>
        ) : null}
      </ConfirmDialog>
    </section>
  )
}
